import { Component, computed, DOCUMENT, inject, OnInit, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Angulartics2Facebook, Angulartics2GoogleAnalytics, Angulartics2GoogleTagManager } from 'angulartics2';
import { CheckForUpdateService } from './core/ngsw/check-for-update.service';
import { UpdateService } from './core/ngsw/update.service';
import { CookieConsentService, COOKIES_CONSENT_KEY } from './core/cookie-consent/cookie-consent.service';
import { Store } from '@ngrx/store';
import * as fromRoot from './core/store/reducers';
import { SetCookieTrackingConsent } from './core/store/core-context/core-context-actions';
import { getSettingsHandled$, isCookieConsentGiven$ } from './core/store/core-context/core-context-helpers';
import { filter, first, map, switchMap } from 'rxjs/operators';
import { DMPAngularticsProviderService } from './core/tracking/dmpangulartics-provider.service';
import { setTheme } from 'ngx-bootstrap/utils';
import { combineLatest, Observable, of } from 'rxjs';
import { BaseParamsService } from '@app/core/base-params.service';
import { LoginLoadingService } from '@app/core/login-loading.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  private readonly loginLoadingService = inject(LoginLoadingService);
  private readonly baseParamsService = inject(BaseParamsService);
  private readonly document = inject(DOCUMENT);

  private readonly settingsLoaded = signal<boolean>(false);
  protected readonly loaded = computed(() => this.settingsLoaded() && !this.loginLoadingService.loginLoading());

  protected readonly isCookieBannerVisible$: Observable<boolean>;

  constructor(translate: TranslateService,
              ga: Angulartics2GoogleAnalytics,
              fb: Angulartics2Facebook,
              gtm: Angulartics2GoogleTagManager,
              private checkUpdateService: CheckForUpdateService,
              private updateService: UpdateService,
              private cookieConsentService: CookieConsentService,
              private store: Store<fromRoot.State>,
              dmpEvents: DMPAngularticsProviderService) {
    setTheme('bs5');
    gtm.startTracking();
    fb.startTracking();
    ga.startTracking();
    dmpEvents.startTracking();

    translate.setFallbackLang('en');

    this.isCookieBannerVisible$ = this.getIsCookeBannerVisible();
  }

  public ngOnInit(): void {
    this.removeGlobalPreloader();

    combineLatest([
      getSettingsHandled$(this.store),
      this.baseParamsService.getParamsResolvedObservable(),
    ]).subscribe(() => {
      this.settingsLoaded.set(true);
      this.setupCookieConsentFromLocalStorage();
    });
    this.checkUpdateService.register();
    this.updateService.register();
  }

  public setupCookieConsentFromLocalStorage(): void {
    if (!this.cookieConsentService.isGiven()) { return; }

    this.store.dispatch(
      new SetCookieTrackingConsent(this.cookieConsentService.isApproved()),
    );
  }

  private getIsCookeBannerVisible(): Observable<boolean> {
    return combineLatest([
      isCookieConsentGiven$(this.store),
      getSettingsHandled$(this.store),
      this.baseParamsService.getParamsResolvedObservable(),
    ]).pipe(
      switchMap(([isConsentGiven]) => {
        if (isConsentGiven) { return of(false); }

        if (this.baseParamsService.isInsideMobileAppWithToken()) {
          return of(false);
        }

        return this.store.select(fromRoot.selectCoreContext).pipe(
          map((s) => s.settings),
          filter((v) => !!v),
          first(),
          map((s) => s.schemaObject.consents.availableConsentsKeys().includes(COOKIES_CONSENT_KEY)),
        );
      }),
    );
  }

  //DEV-853: Prevent background image blink, when added with custom css and loaded is false
  private removeGlobalPreloader(): void {
    const loader = this.document.getElementById('global-loader');
    if (loader) {
      loader.remove();
    }
  }

}
